import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { formatDate, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { Users, Building2, Loader2, Trash2, AlertTriangle, Copy, Key, Search, Phone, Calendar, DoorOpen, UserCheck, UserPlus, Clock, CheckCircle2, MailWarning, CreditCard } from 'lucide-react';

export default function AdminLandlords() {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deactivating, setDeactivating] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmedFilter, setConfirmedFilter] = useState('all'); // all | confirmed | unconfirmed
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const openDetail = async (landlord) => {
    setSelected(landlord);
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await adminAPI.getLandlordDetail(landlord.user_id);
      setDetail(res.data);
    } catch (error) {
      toast.error('Failed to load landlord details');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await adminAPI.getLandlords();
      setLandlords(res.data);
    } catch (error) {
      console.error('Error loading landlords:', error);
      toast.error('Failed to load landlords');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateLandlord = async (landlord) => {
    setDeactivating(landlord.user_id);
    try {
      await adminAPI.deactivateLandlord(landlord.user_id);
      toast.success(`${landlord.name} has been deactivated`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate landlord');
    } finally {
      setDeactivating(null);
    }
  };

  const copyLandlordCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success('Landlord code copied to clipboard');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  // A landlord counts as "unconfirmed" only when the flag is explicitly false
  // (older accounts predating the feature have no flag and are treated as confirmed).
  const isUnconfirmed = (l) => l.email_confirmed === false;

  const filteredLandlords = landlords.filter(l => {
    if (confirmedFilter === 'confirmed' && isUnconfirmed(l)) return false;
    if (confirmedFilter === 'unconfirmed' && !isUnconfirmed(l)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.phone?.toLowerCase().includes(q) ||
      l.landlord_code?.toLowerCase().includes(q)
    );
  });

  const unconfirmedCount = landlords.filter(isUnconfirmed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-landlords-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Landlord Management</h1>
          <p className="text-muted-foreground mt-1">View and manage landlord accounts</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-1 rounded-lg border p-1 bg-muted/30" data-testid="email-confirm-filter">
            <Button
              size="sm"
              variant={confirmedFilter === 'all' ? 'default' : 'ghost'}
              className={`h-8 ${confirmedFilter === 'all' ? 'bg-[#C85A42] hover:bg-[#B04A35] text-white' : ''}`}
              onClick={() => setConfirmedFilter('all')}
              data-testid="filter-all"
            >
              All
            </Button>
            <Button
              size="sm"
              variant={confirmedFilter === 'confirmed' ? 'default' : 'ghost'}
              className={`h-8 ${confirmedFilter === 'confirmed' ? 'bg-[#6B8E6B] hover:bg-[#5a7a5a] text-white' : ''}`}
              onClick={() => setConfirmedFilter('confirmed')}
              data-testid="filter-confirmed"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Confirmed
            </Button>
            <Button
              size="sm"
              variant={confirmedFilter === 'unconfirmed' ? 'default' : 'ghost'}
              className={`h-8 ${confirmedFilter === 'unconfirmed' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
              onClick={() => setConfirmedFilter('unconfirmed')}
              data-testid="filter-unconfirmed"
            >
              <MailWarning className="w-3.5 h-3.5 mr-1" />
              Not confirmed{unconfirmedCount > 0 ? ` (${unconfirmedCount})` : ''}
            </Button>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search landlords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="landlord-search"
            />
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Landlords ({filteredLandlords.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLandlords.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">{searchQuery ? 'No landlords match your search' : 'No landlords registered yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Code</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Properties</th>
                    <th>Member Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLandlords.map((landlord) => (
                    <tr
                      key={landlord.user_id}
                      className={`cursor-pointer hover:bg-muted/40 transition-colors ${landlord.is_active === false ? 'opacity-50' : ''}`}
                      onClick={() => openDetail(landlord)}
                      data-testid={`landlord-row-${landlord.user_id}`}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={landlord.picture} />
                            <AvatarFallback className="bg-[#C85A42] text-white text-sm">
                              {getInitials(landlord.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{landlord.name}</span>
                              {landlord.is_active === false && (
                                <Badge variant="destructive" className="text-xs">Deactivated</Badge>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">{landlord.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {landlord.landlord_code ? (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="font-mono">
                              <Key className="w-3 h-3 mr-1" />
                              {landlord.landlord_code}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => { e.stopPropagation(); copyLandlordCode(landlord.landlord_code); }}
                              data-testid={`copy-landlord-code-${landlord.user_id}`}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td>
                        {isUnconfirmed(landlord) ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid={`email-status-${landlord.user_id}`}>
                            <MailWarning className="w-3 h-3 mr-1" />
                            Not confirmed
                          </Badge>
                        ) : (
                          <Badge className="bg-[#6B8E6B] text-white" data-testid={`email-status-${landlord.user_id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Confirmed
                          </Badge>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5 text-sm">
                          <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                          {landlord.phone || <span className="text-muted-foreground">-</span>}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {landlord.property_count || 0}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(landlord.created_at)}
                        </div>
                      </td>
                      <td>
                        {landlord.is_active !== false && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                disabled={deactivating === landlord.user_id}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`deactivate-landlord-${landlord.user_id}`}
                              >
                                {deactivating === landlord.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-destructive" />
                                  Deactivate Landlord?
                                </AlertDialogTitle>
                                <AlertDialogDescription className="space-y-2">
                                  <p>This will deactivate <strong>{landlord.name}'s</strong> account:</p>
                                  <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                                    <li>They will no longer be able to log in</li>
                                    <li>Their subscription will be cancelled</li>
                                    <li>All properties and data will be preserved</li>
                                  </ul>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeactivateLandlord(landlord)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Deactivate
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="landlord-detail-panel">
          <SheetHeader>
            <SheetTitle>{selected?.name || 'Landlord'}</SheetTitle>
            <SheetDescription>{selected?.email}</SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
            </div>
          ) : detail ? (
            <div className="mt-6 space-y-6">
              {/* Profile */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Profile</h3>
                <div className="rounded-lg border p-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Key className="w-3.5 h-3.5" /> Landlord code</span>
                    <span className="font-mono font-medium">{detail.profile.landlord_code || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Phone</span>
                    <span>{detail.profile.phone || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Member since</span>
                    <span>{detail.profile.created_at ? formatDate(detail.profile.created_at) : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Email confirmed</span>
                    {detail.profile.email_confirmed ? (
                      <Badge className="bg-[#6B8E6B] text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmed</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300"><MailWarning className="w-3 h-3 mr-1" />Not confirmed</Badge>
                    )}
                  </div>
                </div>
              </section>

              {/* Subscription */}
              {detail.subscription && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Subscription</h3>
                  <div className="rounded-lg border p-3 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-[#C85A42]" /> {detail.subscription.status === 'active' ? 'Active' : detail.subscription.status}</span>
                    <Badge variant="secondary">
                      {detail.subscription.is_trial && !detail.subscription.trial_expired ? 'Free until Feb 1, 2027' : (detail.subscription.tier || 'Basic')}
                    </Badge>
                  </div>
                </section>
              )}

              {/* Properties & Units */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Properties &amp; Units</h3>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <StatBox label="Properties" value={detail.properties.count} testid="stat-properties" />
                  <StatBox label="Units" value={detail.properties.units_total} testid="stat-units" />
                  <StatBox label="Occupied" value={detail.properties.occupied} testid="stat-occupied" />
                  <StatBox label="Vacant" value={detail.properties.vacant} testid="stat-vacant" />
                </div>
                {detail.properties.list.length > 0 && (
                  <div className="rounded-lg border divide-y">
                    {detail.properties.list.map((p) => (
                      <div key={p.property_id} className="p-3 flex items-center justify-between text-sm">
                        <div className="min-w-0">
                          <p className="font-medium truncate flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />{p.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{p.address}</p>
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3 shrink-0">
                          <span className="flex items-center gap-1"><DoorOpen className="w-3.5 h-3.5" />{p.units}</span>
                          <span className="text-[#6B8E6B]">{p.occupied} occ</span>
                          <span className="text-amber-600">{p.vacant} vac</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Tenants */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Tenants</h3>
                <div className="grid grid-cols-3 gap-2">
                  <StatBox label="Total" value={detail.tenants.total} icon={<Users className="w-3.5 h-3.5" />} testid="stat-tenants-total" />
                  <StatBox label="App sign-ups" value={detail.tenants.app_signup} icon={<UserCheck className="w-3.5 h-3.5" />} testid="stat-tenants-app" />
                  <StatBox label="Manual" value={detail.tenants.manual} icon={<UserPlus className="w-3.5 h-3.5" />} testid="stat-tenants-manual" />
                  <StatBox label="Pending / invited" value={detail.tenants.pending} icon={<Clock className="w-3.5 h-3.5" />} testid="stat-tenants-pending" />
                  <StatBox label="Active" value={detail.tenants.active} testid="stat-tenants-active" />
                  <StatBox label="Former" value={detail.tenants.former} testid="stat-tenants-former" />
                </div>
              </section>
            </div>
          ) : (
            <div className="mt-6 text-center text-sm text-muted-foreground">No details available.</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatBox({ label, value, icon, testid }) {
  return (
    <div className="rounded-lg border p-3 text-center" data-testid={testid}>
      <div className="text-xl font-bold">{value ?? 0}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center justify-center gap-1">{icon}{label}</div>
    </div>
  );
}
